import {
  Component,
  Input,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subject, Subscription, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { locationKind } from "app/location/location-kind.const";
import { LocationGroup } from "app/location-group/location-group.model";
import { LocationGroupRepository } from "app/location-group/location-group.repository";
import { AutocompleteEmptyMessageComponent } from "../autocomplete-empty-message/autocomplete-empty-message.component";
import { Bind } from "primeng/bind";
import { AutoComplete } from "primeng/autocomplete";
import { FormsModule, NgModel } from "@angular/forms";

@Component({
  selector: "app-location-group-select",
  styleUrls: ["./location-group-select.component.scss"],
  templateUrl: "./location-group-select.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, AutoComplete, FormsModule, AutocompleteEmptyMessageComponent],
})
export class LocationGroupSelectComponent implements OnDestroy {
  private locationGroupRepository = inject(LocationGroupRepository);

  public readonly locationGroupChange = output<LocationGroup>();
  public readonly autocompleteChange = output<boolean>();
  @Input() public locationGroup: LocationGroup = null;
  public readonly appendTo = input<any>(undefined);
  public readonly clearFiltersButton = input(false);
  public readonly required = input(false);
  public readonly disabled = input(false);

  public locationKind = locationKind;
  public subscription = new Subscription();
  public locationGroupList: LocationGroup[] = [];
  public readonly loading = signal(false);

  private searchTerm = new Subject<string>();
  private readonly searchModel = viewChild(NgModel);

  public constructor() {
    this.subscription.add(
      this.searchTerm
        .pipe(
          switchMap((query: string) =>
            this.locationGroupRepository
              .search(query)
              .pipe(catchError(() => of(null))),
          ),
        )
        .subscribe((locationGroups: LocationGroup[] | null) => {
          this.locationGroupList = locationGroups ?? [];
          this.loading.set(false);
          this.autocompleteChange.emit(true);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // The control is required, so once it has been touched an empty value paints
  // it invalid. Callers that clear it after a successful save reset that.
  public resetTouchedState(): void {
    const control = this.searchModel()?.control;
    control?.markAsUntouched();
    control?.markAsPristine();
  }

  public onLocationGroupChange(event: LocationGroup) {
    this.locationGroupChange.emit(event);
  }

  public search(event: { originalEvent: KeyboardEvent; query: string }): void {
    // new array reference opens the overlay so the loading message is visible
    this.locationGroupList = [];
    this.loading.set(true);
    this.searchTerm.next(event.query);
  }
}
